import { X } from "lucide-react";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";

type Position = "top" | "bottom" | "left" | "right";

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: Position;
}

type TooltipAlign = "start" | "center" | "end";

interface TourTooltipProps {
  targetSelector: string;
  titleKey: string;
  textKey: string;
  position: Position;
  tooltipAlign?: TooltipAlign;
  arrowOffset?: number;
  isVisible: boolean;
  showSkip?: boolean;
  showNext?: boolean;
  showDone?: boolean;
  onSkip?: () => void;
  onNext?: () => void;
  onDone?: () => void;
}

const TOOLTIP_WIDTH = 320;
const TOOLTIP_OFFSET = 16;
const ARROW_SIZE = 8;

export const TourTooltip: FC<TourTooltipProps> = ({
  targetSelector,
  titleKey,
  textKey,
  position,
  tooltipAlign = "center",
  arrowOffset,
  isVisible,
  showSkip = true,
  showNext = false,
  showDone = false,
  onSkip,
  onNext,
  onDone,
}) => {
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>(null);

  const calculatePosition = useCallback(() => {
    const element = document.querySelector(targetSelector);
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    // For top/bottom positions, arrow should point toward the target
    // When tooltip is above target (position="top"), arrow should be at bottom pointing down
    // When tooltip is below target (position="bottom"), arrow should be at top pointing up
    let arrowPosition: Position =
      position === "top"
        ? "bottom"
        : position === "bottom"
          ? "top"
          : position === "left"
            ? "right"
            : "left";

    const estimatedTooltipHeight = 180;

    switch (position) {
      case "top":
        top = rect.top - estimatedTooltipHeight - TOOLTIP_OFFSET;
        left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case "bottom":
        top = rect.bottom + TOOLTIP_OFFSET;
        left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case "left":
        if (tooltipAlign === "start") {
          top = rect.top - 16;
        } else if (tooltipAlign === "end") {
          top = rect.bottom - estimatedTooltipHeight + 16;
        } else {
          top = rect.top + rect.height / 2 - estimatedTooltipHeight / 2;
        }
        left = rect.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET;
        break;
      case "right":
        if (tooltipAlign === "start") {
          top = rect.top - 16;
        } else if (tooltipAlign === "end") {
          top = rect.bottom - estimatedTooltipHeight + 16;
        } else {
          top = rect.top + rect.height / 2 - estimatedTooltipHeight / 2;
        }
        left = rect.right + TOOLTIP_OFFSET;
        break;
    }

    if (left < 16) {
      left = 16;
    } else if (left + TOOLTIP_WIDTH > viewportWidth - 16) {
      left = viewportWidth - TOOLTIP_WIDTH - 16;
    }

    if (top < 16) {
      top = 16;
      if (position === "top") {
        // Flip to bottom: tooltip now below target, arrow at top pointing up
        arrowPosition = "top";
        top = rect.bottom + TOOLTIP_OFFSET;
      }
    } else if (top + estimatedTooltipHeight > viewportHeight - 16) {
      if (position === "bottom") {
        // Flip to top: tooltip now above target, arrow at bottom pointing down
        arrowPosition = "bottom";
        top = rect.top - estimatedTooltipHeight - TOOLTIP_OFFSET;
      }
    }

    return { top, left, arrowPosition };
  }, [targetSelector, position, tooltipAlign]);

  useEffect(() => {
    if (!isVisible) {
      setTooltipPosition(null);
      return;
    }

    const updatePosition = () => {
      const pos = calculatePosition();
      setTooltipPosition(pos);
    };

    updatePosition();
    const timeoutId = setTimeout(updatePosition, 300);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isVisible, calculatePosition]);

  if (!isVisible || !tooltipPosition) {
    return null;
  }

  const getArrowStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: "absolute",
      width: 0,
      height: 0,
      borderStyle: "solid",
    };

    const getVerticalPosition = (): React.CSSProperties => {
      if (arrowOffset !== undefined) {
        return { top: arrowOffset };
      }
      return { top: "50%", transform: "translateY(-50%)" };
    };

    const getHorizontalPosition = (): React.CSSProperties => {
      if (arrowOffset !== undefined) {
        return { left: arrowOffset };
      }
      return { left: "50%", transform: "translateX(-50%)" };
    };

    switch (tooltipPosition.arrowPosition) {
      case "top":
        return {
          ...baseStyles,
          top: -ARROW_SIZE,
          ...getHorizontalPosition(),
          borderWidth: `0 ${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px`,
          borderColor: "transparent transparent hsl(var(--card)) transparent",
        };
      case "bottom":
        return {
          ...baseStyles,
          bottom: -ARROW_SIZE,
          ...getHorizontalPosition(),
          borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px 0 ${ARROW_SIZE}px`,
          borderColor: "hsl(var(--card)) transparent transparent transparent",
        };
      case "left":
        return {
          ...baseStyles,
          left: -ARROW_SIZE,
          ...getVerticalPosition(),
          borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px 0`,
          borderColor: "transparent hsl(var(--card)) transparent transparent",
        };
      case "right":
        return {
          ...baseStyles,
          right: -ARROW_SIZE,
          ...getVerticalPosition(),
          borderWidth: `${ARROW_SIZE}px 0 ${ARROW_SIZE}px ${ARROW_SIZE}px`,
          borderColor: "transparent transparent transparent hsl(var(--card))",
        };
    }
  };

  return (
    <div
      className="fixed z-9999"
      style={{
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        width: TOOLTIP_WIDTH,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-tooltip-title"
      aria-describedby="tour-tooltip-description"
    >
      <Card className="relative shadow-lg border">
        <div style={getArrowStyles()} aria-hidden="true" />

        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <CardTitle id="tour-tooltip-title" className="text-base">
              <FormattedMessage id={titleKey} />
            </CardTitle>
            <button
              onClick={onSkip}
              className="text-muted-foreground hover:text-foreground transition-colors -mt-1 -mr-1 p-1"
              aria-label="Close tour"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </CardHeader>

        <CardContent className="pb-4">
          <CardDescription id="tour-tooltip-description" className="text-sm">
            <FormattedMessage id={textKey} />
          </CardDescription>
        </CardContent>

        <CardFooter className="flex justify-end gap-2 pt-0">
          {showSkip && !showDone && (
            <Button variant="ghost" size="sm" onClick={onSkip}>
              <FormattedMessage id="onboarding.button.skip" />
            </Button>
          )}
          {showNext && (
            <Button size="sm" onClick={onNext}>
              <FormattedMessage id="onboarding.button.next" />
            </Button>
          )}
          {showDone && (
            <Button size="sm" onClick={onDone}>
              <FormattedMessage id="onboarding.button.done" />
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};
